import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Snackbar,
  Alert,
  Avatar,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NoPhotographyOutlinedIcon from "@mui/icons-material/NoPhotographyOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SaveIcon from "@mui/icons-material/Save";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NoteIcon from "@mui/icons-material/Note";

import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import { useCustomers } from "../../hooks/useCustomers";
import { useProductsBranch } from "../../hooks/useProductsBranch";
import * as services from "../../services/saleng";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { formatNumber } from "../../utils/formatNumber";
import ProductSelectDialog from "../../components/ProductSelectDialog";
import { jwtDecode } from "jwt-decode";
import { hasPermission } from "../../auth/RequirePermission3";

const getAccessToken = () => localStorage.getItem("accessToken") || null;

const decodeToken = () => {
  const token = getAccessToken();
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

const getTokenData = () => {
  const decoded = decodeToken();
  return {
    role: decoded?.role ?? null,
  };
};

const PoManagementCreate = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];

  // --- Data Hooks ---
  const { customers, loading: loadingCust } = useCustomers();
  const { products, loading: loadingProd } = useProductsBranch();

  // --- States ---
  const [rowsData, setRowsData] = useState([]);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 30);
    return d.toISOString().split("T")[0];
  });

  const [openCustDialog, setOpenCustDialog] = useState(false);
  const [modalStep, setModalStep] = useState("customer");
  const [selCustomer, setSelCustomer] = useState(null);
  const [selCar, setSelCar] = useState(null);
  const [snackNoCar, setSnackNoCar] = useState(false);
  const [openProdDialog, setOpenProdDialog] = useState(false);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState("");
  const [detail2, setDetail2] = useState("");
  const [searchParams] = useSearchParams();
  const [searchCustomer, setSearchCustomer] = useState("");
  const [adjustments, setAdjustments] = useState([]);

  // const poId = searchParams.get("id"); // Not used in create?
  const { role } = getTokenData();

  const statusFilter = "draft";
  const statusMap = {
    open: "เปิด",
    close: "สิ้นสุด",
    cancel: "ยกเลิก",
    draft: "ร่าง",
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "bg-green-100 text-green-700 border-green-200";
      case "close":
        return "bg-red-100 text-red-700 border-red-200";
      case "draft":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  useEffect(() => {
    if (!startDate) return;
    const d = new Date(startDate);
    d.setDate(d.getDate() + 14);
    setEndDate(d.toISOString().split("T")[0]);
  }, [startDate]);

  useEffect(() => {
    if (!selCustomer) return;

    const fetchAdjustments = async () => {
      try {
        const adjData = await services.getProductGroupsAdjustments(
          selCustomer.customerGroupId
        );
        setAdjustments(adjData);
      } catch (error) {
        console.error("Error fetching adjustments:", error);
      }
    };

    fetchAdjustments();
  }, [selCustomer]);

  const getMinEndDate = (start) => {
    if (!start) return "";
    const d = new Date(start);
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  };

  const categories = useMemo(() => {
    const seen = new Set();
    return products
      .map((p) => p.category)
      .filter((cat) => cat?.name && !seen.has(cat.name) && seen.add(cat.name));
  }, [products]);

  const filteredRows = useMemo(() => {
    const normSearch = search.trim().toLowerCase();

    const filtered = rowsData.filter((r) =>
      r.product?.toLowerCase().includes(normSearch)
    );

    return filtered.sort((a, b) => {
      const A = a?.itemCode ?? "";
      const B = b?.itemCode ?? "";
      if (!A && B) return 1;
      if (A && !B) return -1;
      return String(A).localeCompare(String(B), "th", {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [rowsData, search]);

  // summary row calculation
  const summaryData = useMemo(() => {
    const sumActual = filteredRows.reduce((s, r) => s + (r.actualWeight ?? 0), 0);
    const sumPo = filteredRows.reduce((s, r) => s + (r.poWeight ?? 0), 0);
    const sumShort = filteredRows.reduce((s, r) => s + (r.shortWeight ?? 0), 0);
    const sumPredict = filteredRows.reduce((s, r) => s + (r.predictPrice ?? 0), 0);
    return {
        sumActual,
        sumPo,
        sumShort,
        sumPredict
    };
  }, [filteredRows]);

  // Combined logic is handled in rendering now

  const handleConfirmProducts = (selectedProducts) => {
    const arr = Array.isArray(selectedProducts)
      ? selectedProducts
      : selectedProducts == null
        ? []
        : [selectedProducts];

    const pickedIds = arr
      .map((x) => (typeof x === "number" ? x : x?.id))
      .filter((v) => Number.isFinite(v));

    setRowsData((prev) => {
      const byProd = new Map(prev.map((r) => [r.productId, r]));

      for (const pid of pickedIds) {
        const pObjFromArg = arr.find((x) =>
          typeof x === "object" ? x.id === pid : false
        );
        const p = pObjFromArg || products.find((x) => x.id === pid);
        if (!p) continue;

        const existing = byProd.get(pid);

        // หาค่า adjustment จากตาราง adjustments (ถ้าไม่มีให้เป็น 0)
        const adj =
          adjustments.find((a) => a.productId === pid)?.adjustment ?? 0;

        const purchasePrice =
          existing?.purchasePrice ?? (p.finalPrice ?? 0) + adj;

        const base = {
          itemId: existing?.itemId ?? null, // null = แถวใหม่
          itemCode: p?.history?.itemCode,
          productId: pid,
          detail: p.subCategory?.name || "",
          product: p.title || "-",
          unit: "กก.",
          unlimitedWeight: existing?.unlimitedWeight ?? false,
          actualWeight: existing?.actualWeight ?? 0,
          poWeight: existing?.poWeight ?? p.poWeight ?? 0,
          shortWeight: existing?.shortWeight ?? 0,
          purchasePrice,
          adjustment: adj, // เก็บค่า adjustment ไว้ด้วย (optional)
        };

        const predictPrice =
          (base.unlimitedWeight ? 0 : Number(base.poWeight) || 0) *
          (Number(base.purchasePrice) || 0);

        byProd.set(pid, { ...base, predictPrice });
      }

      return Array.from(byProd.values());
    });

    setOpenProdDialog(false);
    setSearch("");
  };

  const updateRow = (id, updates) => {
    setRowsData((prev) =>
      prev.map((r) => {
        if ((r.itemId ?? `new-${r.productId}`) !== id) return r;

        // Merge updates
        const next = { ...r, ...updates };
        
        // Recalculate logic (similar to processRowUpdate)
        const N = (v) => (Number.isFinite(+v) ? +v : 0);
        
        // Logic: if unlimited, weight/short is 0
        if (next.unlimitedWeight) {
            next.poWeight = 0;
            next.shortWeight = 0;
            next.predictPrice = 0;
        } else {
            // Recalc
            // Note: In DataGrid processRowUpdate we had:
            // predictPrice = poWeight * purchasePrice
            next.poWeight = N(next.poWeight);
            next.purchasePrice = N(next.purchasePrice);
            next.predictPrice = next.poWeight * next.purchasePrice;
            
            // shortWeight usually = poWeight - actualWeight
            next.shortWeight = next.poWeight - next.actualWeight;
        }

        return next;
      })
    );
  };

  const handleUnlimited = (rowId, chk) => {
      updateRow(rowId, { unlimitedWeight: chk });
  };

  const handleBulkUnlimited = (checked) => {
      setRowsData(prev => prev.map(r => {
          const N = (v) => (Number.isFinite(+v) ? +v : 0);
          const next = { ...r, unlimitedWeight: checked };
          if (checked) {
              next.poWeight = 0;
              next.shortWeight = 0;
              next.predictPrice = 0;
          } else {
              // Restore or recalc? 
              // If we uncheck, we might want to keep the values as they were or reset?
              // The original logic in `renderHeader` `onChange`:
              // poWeight: chk ? 0 : x.poWeight
              // So if unchecking (chk=false), it restores x.poWeight (which might have been 0 if we didn't save prev state)
              // Wait, in React state updates, if we set poWeight to 0 when checking, we lose the old value.
              // The original code:
              // poWeight: chk ? 0 : x.poWeight (This uses current state x.poWeight)
              // So if I check it, x.poWeight becomes 0. If I uncheck it immediately, it stays 0.
              // This is how the original worked, so I will stick to it.
              next.poWeight = next.poWeight; 
              next.shortWeight = next.poWeight - next.actualWeight;
              next.predictPrice = next.poWeight * next.purchasePrice;
          }
          return next;
      }));
  };

  const handleDelete = (rowId) => {
      setRowsData(prev => prev.filter(r => (r.itemId ?? `new-${r.productId}`) !== rowId));
  };

  const handleSave = async () => {
    const errors = [];
    if (!endDate) errors.push("กรุณากรอกวันที่สิ้นสุด");
    if (!selCustomer) errors.push("กรุณาเลือกลูกค้า");
    if (!rowsData.length) errors.push("กรุณาเลือกสินค้าอย่างน้อย 1 รายการ");
    if (errors.length) {
      errors.forEach((err) => toast.error(err));
      return;
    }

    try {
      const N = (v) => (Number.isFinite(+v) ? +v : 0);

      const itemsPayload = rowsData.map((r) => {
        const base = {
          productId: Number(r.productId),
          poWeight: N(r.poWeight),
          actualWeight: N(r.actualWeight),
          shortWeight: N(r.shortWeight),
          purchasePrice: N(r.purchasePrice),
          predictPrice: N(r.predictPrice),
          unlimitedWeight: !!r.unlimitedWeight,
        };
        return r.itemId ? { id: Number(r.itemId), ...base } : base;
      });

      const payload = {
        customerId: Number(selCustomer?.id),
        carId: selCar?.id ?? null,
        startDate,
        endDate: endDate || null,
        detail: detail ?? null,
        detail2: detail2 ?? null,
        status: "open",
        items: itemsPayload,
      };

      let res = await services.createPurchaseOrders(payload);
      toast.success("บันทึกสำเร็จ");
      resetForm();
      let id = res.data.data.id;
      setTimeout(() => {
        navigate(`/po_management/po_edit?id=${id}`);
      }, 800);
    } catch (err) {
      console.error(err);
      toast.error("เกิดข้อผิดพลาดในการบันทึก");
    }
  };

  const resetForm = () => {
    const todayStr = new Date().toISOString().split("T")[0];
    const d = new Date(todayStr);
    d.setDate(d.getDate() + 30);
    const endStr = d.toISOString().split("T")[0];

    setRowsData([]);
    setSearch("");
    setDetail("");
    setDetail2("");

    setSelCustomer(null);
    setSelCar(null);

    setStartDate(todayStr);
    setEndDate(endStr);

    setOpenProdDialog(false);
    setOpenCustDialog(false);

    setSearchCustomer("");
    setAdjustments([]);
  };

  if (loadingCust || loadingProd) return <LinearProgress />;

  const allChecked = rowsData.length > 0 && rowsData.every(r => r.unlimitedWeight);
  const someChecked = rowsData.some(r => r.unlimitedWeight) && !allChecked;

  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        <AppBreadcrumbs />
        <Toaster position="bottom-left" reverseOrder={false} />

        {/* 1. Header Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                 <AddIcon />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                สร้างใบสั่งซื้อใหม่
              </h1>
            </div>
            <p className="text-sm text-gray-500 ml-11">
              ระบุข้อมูลลูกค้ารายการสินค้าเพื่อสร้างใบสั่งซื้อ
            </p>
          </div>
          
          <div className={`px-4 py-1.5 rounded-full text-sm font-bold border ${getStatusColor(statusFilter)} flex items-center gap-2`}>
             <span className="w-2 h-2 rounded-full bg-current opacity-60"></span>
             {statusMap[statusFilter]}
          </div>
        </div>

        {/* 2. Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer Card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col h-full relative group">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">ข้อมูลลูกค้า</h3>
              {selCustomer ? (
                  <div className="flex-1 flex flex-col gap-4">
                      <div className="flex items-center gap-4">
                          <Avatar
                              src={selCustomer.img}
                              sx={{ width: 64, height: 64 }}
                              className="shadow-sm border-4 border-slate-50"
                          >
                              <NoPhotographyOutlinedIcon />
                          </Avatar>
                          <div>
                              <p className="font-bold text-lg text-gray-900 line-clamp-1">{selCustomer.name}</p>
                              <p className="text-sm text-gray-500 flex items-center gap-1">
                                  <DirectionsCarIcon fontSize="inherit" />
                                  {selCar ? `ทะเบียน ${selCar.license}` : "ไม่ระบุรถ"}
                              </p>
                          </div>
                      </div>
                      
                      <div className="mt-auto flex gap-2">
                          <button
                              onClick={() => {
                                  setModalStep("customer");
                                  setOpenCustDialog(true);
                              }}
                              className="flex-1 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                          >
                              เปลี่ยนลูกค้า
                          </button>
                          <button 
                              onClick={() => {
                                  setSelCustomer(null);
                                  setSelCar(null);
                                  setModalStep("customer");
                              }}
                              className="px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
                          >
                              <DeleteIcon fontSize="small" />
                          </button>
                      </div>
                  </div>
              ) : (
                  <div className="flex-1 flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-200 rounded-xl bg-slate-50 hover:bg-blue-50/50 hover:border-blue-300 transition-all cursor-pointer group/add w-full h-[180px]"
                       onClick={() => setOpenCustDialog(true)}>
                      <div className="p-3 bg-white rounded-full shadow-sm group-hover/add:scale-110 transition-transform text-gray-400 group-hover/add:text-blue-500">
                          <PersonIcon fontSize="large" />
                      </div>
                      <span className="mt-3 text-sm font-medium text-gray-500 group-hover/add:text-blue-600">กดเพื่อเลือกลูกค้า</span>
                  </div>
              )}
          </div>

          {/* Details & Dates */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col h-full">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">เงื่อนไขและวันที่</h3>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                   วันที่: {today}
                </span>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 flex-1">
                  <div className="space-y-4">
                      <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">รายละเอียดเอกสาร</label>
                          <textarea
                             rows={3}
                             className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm text-gray-700 resize-none"
                             placeholder="ระบุรายละเอียดเพิ่มเติม..."
                             value={detail}
                             onChange={(e) => setDetail(e.target.value)}
                          />
                      </div>
                  </div>
                  
                  <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">วันเริ่มต้น</label>
                            <div className="relative">
                                <input
                                  type="date"
                                  value={startDate}
                                  onChange={(e) => setStartDate(e.target.value)}
                                  disabled
                                  className="w-full pl-10 pr-4 py-2.5 bg-gray-100 border border-slate-200 rounded-xl text-sm font-medium text-gray-500"
                                />
                                <CalendarTodayIcon className="absolute left-3 top-2.5 w-4 h-4 text-gray-400 opacity-70" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">วันสิ้นสุด</label>
                            <div className="relative">
                                <input
                                  type="date"
                                  value={endDate}
                                  onChange={(e) => setEndDate(e.target.value)}
                                  min={getMinEndDate(startDate)}
                                  disabled={!["Manager", "Admin"].includes(role)}
                                  className={`w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none
                                     ${!["Manager", "Admin"].includes(role) ? "bg-gray-100 cursor-not-allowed text-gray-500" : "bg-white"}`}
                                />
                                <CalendarTodayIcon className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
        </div>

        {/* 3. Product Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col min-h-[400px]">
           {/* Toolbar */}
           <div className="p-4 sm:p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4 bg-white sticky top-0 z-10">
               <div className="flex items-center gap-3 w-full sm:w-auto">
                    <h3 className="text-lg font-bold text-gray-800">รายการสินค้า</h3>
                    <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-bold">
                        {rowsData.length} รายการ
                    </span>
               </div>
               
               <div className="flex items-center gap-3 w-full sm:w-auto">
                   <div className="relative group w-full sm:w-64">
                       <SearchIcon className="absolute left-3 top-2.5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                       <input 
                           type="text" 
                           placeholder="ค้นหาสินค้า..." 
                           className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-transparent hover:bg-white hover:border-gray-200 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl text-sm outline-none transition-all"
                           value={search}
                           onChange={(e) => setSearch(e.target.value)}
                       />
                   </div>
                   
                   <button
                        onClick={() => setOpenProdDialog(true)}
                        className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all active:scale-95 flex items-center gap-2 whitespace-nowrap"
                   >
                        <AddIcon className="w-5 h-5" />
                        <span className="hidden sm:inline">เพิ่มสินค้า</span>
                   </button>
               </div>
           </div>

           {/* Table */}
           <div className="overflow-x-auto custom-scrollbar flex-1 bg-white">
                <table className="w-full min-w-[1000px]">
                  <thead>
                    <tr className="bg-gray-50/80 border-b border-gray-100 text-left">
                      <th className="px-4 py-4 w-24">
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">รหัส</span>
                      </th>
                      <th className="px-4 py-4 w-32">
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">หมวดหมู่</span>
                      </th>
                      <th className="px-6 py-4">
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">สินค้า</span>
                      </th>
                      <th className="px-4 py-4 text-center w-36">
                        <div className="flex flex-col items-center gap-1 cursor-pointer group" onClick={() => handleBulkUnlimited(!allChecked)}>
                            <div className="flex items-center gap-2 px-2 py-1 rounded-lg group-hover:bg-gray-200/50 transition-colors">
                                <Checkbox
                                    checked={allChecked}
                                    indeterminate={someChecked}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                    inputProps={{ "aria-label": "Select all unlimited" }}
                                    onClick={(e) => e.stopPropagation()} 
                                    onChange={(e) => handleBulkUnlimited(e.target.checked)}
                                />
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">ไม่จำกัด</span>
                            </div>
                        </div>
                      </th>
                      <th className="px-4 py-4 text-right w-40">
                         <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">นน.จริง / PO</span>
                      </th>
                      <th className="px-4 py-4 text-right w-32">
                         <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">ส่วนต่าง</span>
                      </th>
                      <th className="px-4 py-4 text-right w-40">
                         <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">ราคาซื้อ</span>
                      </th>
                      <th className="px-6 py-4 text-right w-48">
                         <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">รวม (คาดการณ์)</span>
                      </th>
                      <th className="px-4 py-4 w-16 text-center"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredRows.length === 0 ? (
                        <tr>
                            <td colSpan="9" className="px-6 py-20 text-center text-gray-400 bg-white">
                                <div className="flex flex-col items-center gap-4">
                                    <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center">
                                        <LocalShippingIcon sx={{ fontSize: 32, opacity: 0.3 }} />
                                    </div>
                                    <span className="font-medium">ยังไม่มีรายการสินค้า</span>
                                    <button onClick={() => setOpenProdDialog(true)} className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                                        + เพิ่มสินค้าเลย
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ) : (
                        filteredRows.map((row, index) => {
                            const rowId = row.itemId ?? `new-${row.productId}`;
                            // Determine if weight is editable (same logic as in Edit page: standard permission if not unlimited)
                            // In Create page, weight is usually editable unless unlimited
                            const isWeightEditable = !row.unlimitedWeight;
                            
                            return (
                                <tr key={rowId} className={`group hover:bg-blue-50/20 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                                    <td className="px-4 py-4">
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:text-blue-700 transition-colors">
                                            {row.itemCode || "-"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <span className="text-sm font-medium text-gray-500">
                                            {row.detail}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">{row.product}</span>
                                            <span className="text-xs text-gray-400 mt-0.5">หน่วย: {row.unit}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-center">
                                         <div className="flex justify-center">
                                            <Checkbox 
                                                size="small"
                                                checked={!!row.unlimitedWeight}
                                                onChange={(e) => handleUnlimited(rowId, e.target.checked)}
                                                sx={{ 
                                                    '&.Mui-checked': { color: '#2563eb' },
                                                }}
                                            />
                                         </div>
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="flex items-center justify-end gap-3">
                                            <span className="text-sm font-medium text-gray-500">{formatNumber(row.actualWeight)}</span>
                                            <span className="text-gray-300 font-light">/</span>
                                            {row.unlimitedWeight ? (
                                                <span className="text-sm text-blue-600 font-bold bg-blue-50 px-2 py-0.5 rounded">Unlimited</span>
                                            ) : (
                                                <div className="relative w-28">
                                                    {isWeightEditable ? (
                                                        <input
                                                            type="number"
                                                            value={row.poWeight}
                                                            onChange={(e) => updateRow(rowId, { poWeight: e.target.value })}
                                                            onFocus={(e) => e.target.select()}
                                                            className="w-full px-3 py-1.5 text-right text-sm font-bold bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-gray-900 shadow-sm transition-all"
                                                        />
                                                    ) : (
                                                        <span className="text-sm font-bold text-gray-900 block text-right px-3 py-1.5 bg-gray-50 rounded-lg border border-transparent">
                                                            {formatNumber(row.poWeight)}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-right">
                                        {row.unlimitedWeight ? (
                                             <span className="text-gray-300">-</span>
                                        ) : (
                                            <span className={`text-sm font-bold px-2 py-1 rounded ${
                                                formatNumber(row.shortWeight).includes("-") 
                                                ? "bg-red-50 text-red-600" 
                                                : "text-gray-700"
                                            }`}>
                                                {formatNumber(row.shortWeight)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-4">
                                         <div className="flex justify-end">
                                             {hasPermission(["po_edit_price"]) ? (
                                                 <div className="relative w-32">
                                                     <input
                                                        type="number"
                                                        value={row.purchasePrice}
                                                        onChange={(e) => updateRow(rowId, { purchasePrice: e.target.value })}
                                                        onFocus={(e) => e.target.select()}
                                                        className="w-full px-3 py-1.5 text-right text-sm font-bold bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-blue-700 shadow-sm transition-all"
                                                    />
                                                    <span className="absolute left-2 top-1.5 text-gray-400 text-xs font-light">฿</span>
                                                 </div>
                                             ) : (
                                                 <span className="text-sm text-gray-900 font-bold">฿ {formatNumber(row.purchasePrice)}</span>
                                             )}
                                         </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <span className="text-base font-bold text-gray-900 block">
                                            {formatNumber(row.predictPrice)}
                                        </span>
                                        <span className="text-[10px] text-gray-400 uppercase font-semibold tracking-wide">THB</span>
                                    </td>
                                    <td className="px-4 py-4 text-center">
                                        <button 
                                            onClick={() => handleDelete(rowId)}
                                            className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                  </tbody>
                  {filteredRows.length > 0 && (
                       <tfoot className="bg-slate-50 border-t border-gray-200">
                           <tr className="divide-x divide-gray-200/50">
                               <td className="px-6 py-4 text-right" colSpan={4}>
                                   <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">รวมท้ายตาราง</span>
                               </td>
                               <td className="px-4 py-4 text-right">
                                   <div className="flex flex-col gap-1 items-end">
                                       <div className="flex items-center gap-2 text-xs text-gray-400 font-medium uppercase">
                                           <span>จริง</span>
                                           <span className="w-12 text-right text-gray-600 font-bold">{formatNumber(summaryData.sumActual)}</span>
                                       </div>
                                       <div className="flex items-center gap-2 text-xs text-gray-400 font-medium uppercase">
                                           <span>PO</span>
                                           <span className="w-12 text-right text-gray-900 font-bold">{formatNumber(summaryData.sumPo)}</span>
                                       </div>
                                   </div>
                               </td>
                               <td className="px-4 py-4 text-right align-top">
                                   <span className="text-sm font-bold text-red-600">{formatNumber(summaryData.sumShort)}</span>
                               </td>
                               <td className="px-4 py-4 text-right align-top text-gray-400">-</td>
                               <td className="px-6 py-4 text-right align-top bg-blue-50/30">
                                   <span className="text-xl font-bold text-blue-700">{formatNumber(summaryData.sumPredict)}</span>
                                   <span className="text-xs text-blue-400 block font-semibold mt-1">TOTAL THB</span>
                               </td>
                               <td></td>
                           </tr>
                       </tfoot>
                  )}
                </table>
           </div>
        </div>

        {/* 4. Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-24">
            {/* Left: Remarks */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col gap-3">
                 <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">ข้อความปิดท้าย / หมายเหตุ</h3>
                 <textarea
                     rows={6}
                     className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm text-gray-700 resize-none"
                     placeholder="ระบุข้อความเพิ่มเติม..."
                     value={detail2}
                     onChange={(e) => setDetail2(e.target.value)}
                 />
            </div>

            {/* Right: Summary */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col justify-center">
                 <div className="space-y-4">
                     <div className="flex justify-between items-center pb-4 border-b border-dashed border-gray-100">
                         <span className="text-gray-500 font-medium">รวมน้ำหนัก PO ทั้งหมด</span>
                         <span className="text-lg font-bold text-gray-900">{formatNumber(summaryData.sumPo)} <span className="text-sm text-gray-400 font-normal">กก.</span></span>
                     </div>
                     <div className="flex justify-between items-center pb-4 border-b border-dashed border-gray-100">
                         <span className="text-gray-500 font-medium">รวมน้ำหนักจริง</span>
                         <span className="text-lg font-bold text-gray-900">{formatNumber(summaryData.sumActual)} <span className="text-sm text-gray-400 font-normal">กก.</span></span>
                     </div>
                     <div className="flex justify-between items-center pt-2">
                         <span className="text-lg font-bold text-gray-800">ราคารวมโดยประมาณ</span>
                         <div className="text-right">
                             <span className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                                 {formatNumber(summaryData.sumPredict)}
                             </span>
                             <span className="text-sm text-gray-400 font-bold ml-2">THB</span>
                         </div>
                     </div>
                 </div>
            </div>
        </div>

        {/* 5. Sticky Footer */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-md border-t border-gray-200 z-40 flex justify-center shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
             <div className="max-w-[1600px] w-full flex justify-end items-center gap-3">
                 <button
                   onClick={() => navigate(-1)}
                   className="px-8 py-3 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 font-bold rounded-2xl shadow-sm transition-all active:scale-95"
                 >
                    ยกเลิก
                 </button>
                 <button
                   onClick={handleSave}
                   className="px-10 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold rounded-2xl shadow-lg shadow-green-200 hover:shadow-green-300 transition-all active:scale-95 flex items-center gap-3"
                 >
                    <SaveIcon />
                    บันทึกใบเสนอราคา
                 </button>
             </div>
        </div>

        <ProductSelectDialog
            open={openProdDialog}
            onClose={() => setOpenProdDialog(false)}
            onConfirm={handleConfirmProducts}
            products={products}
            categories={categories}
            multi
        />

        {/* Dialog: เลือกลูกค้า → รถ (Updated Design) */}
        <Dialog
            open={openCustDialog}
            onClose={() => setOpenCustDialog(false)}
            fullWidth
            maxWidth="xl"
            PaperProps={{
                sx: { borderRadius: "16px", height: "80vh" }
            }}
        >
            <DialogTitle className="bg-white border-b border-slate-100 p-6">
                <div className="flex justify-between items-center">
                    {modalStep === "customer" ? (
                        <>
                            <h2 className="text-2xl font-bold text-gray-800">เลือกลูกค้า</h2>
                            <div className="relative w-80">
                                <input
                                    type="text"
                                    placeholder="ค้นหาชื่อ..."
                                    value={searchCustomer}
                                    onChange={(e) => setSearchCustomer(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 bg-slate-100 border-none rounded-xl focus:ring-2 focus:ring-blue-500/20 text-sm transition-all"
                                />
                                <SearchIcon className="absolute left-3 top-2.5 text-gray-400" />
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center gap-3">
                             <button onClick={() => setModalStep("customer")} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                                 <ArrowBackIcon />
                             </button>
                             <h2 className="text-2xl font-bold text-gray-800">เลือกรถของ <span className="text-blue-600">{selCustomer?.name}</span></h2>
                        </div>
                    )}
                </div>
            </DialogTitle>
            <DialogContent dividers className="bg-slate-50/50 p-6">
                {modalStep === "customer" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {customers
                            .filter((c) => c.name.toLowerCase().includes(searchCustomer.toLowerCase()))
                            .map((c) => (
                                <div 
                                    key={c.id} 
                                    className="bg-white p-5 border border-slate-200 rounded-2xl hover:border-blue-400 hover:shadow-xl hover:shadow-blue-500/10 cursor-pointer transition-all flex items-center gap-4 group"
                                    onClick={() => {
                                        setSelCustomer(c);
                                        if (c.cars?.length) {
                                            setModalStep("car");
                                        } else {
                                            setSelCar(null);
                                            setOpenCustDialog(false);
                                            setSnackNoCar(true);
                                        }
                                    }}
                                >
                                    <Avatar src={c.img} sx={{ width: 64, height: 64 }} className="group-hover:scale-105 transition-transform border-4 border-slate-50">
                                         <PersonIcon />
                                    </Avatar>
                                    <div>
                                        <p className="font-bold text-lg text-gray-900 group-hover:text-blue-700 transition-colors line-clamp-1">{c.name}</p>
                                        <p className="text-sm text-gray-400 group-hover:text-gray-500 mt-1">
                                            {c.personType === "corporate" ? "นิติบุคคล" : "บุคคลธรรมดา"}
                                        </p>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                         {selCustomer?.cars?.map((car) => (
                             <div 
                                key={car.id}
                                className="bg-white p-6 border border-slate-200 rounded-2xl hover:border-blue-400 hover:bg-blue-50/30 cursor-pointer transition-all flex items-center gap-4 group"
                                onClick={() => {
                                    setSelCar(car);
                                    setOpenCustDialog(false);
                                }}
                             >
                                 <div className="p-3 bg-blue-100 text-blue-600 rounded-xl group-hover:scale-110 transition-transform">
                                     <DirectionsCarIcon fontSize="medium" />
                                 </div>
                                 <div>
                                     <p className="font-bold text-xl text-gray-900 group-hover:text-blue-700">{car.license}</p>
                                     <p className="text-sm text-gray-400">{car.type} — {car.province}</p>
                                 </div>
                             </div>
                         ))}
                    </div>
                )}
            </DialogContent>
            {modalStep === "car" && (
                <DialogActions className="p-4 bg-white border-t border-slate-100">
                    <button 
                        onClick={() => { setSelCar(null); setOpenCustDialog(false); }}
                        className="px-6 py-2.5 text-gray-500 hover:bg-slate-100 rounded-xl font-bold text-sm transition-all"
                    >
                        ไม่ระบุรถ (ข้ามขั้นตอน)
                    </button>
                </DialogActions>
            )}
        </Dialog>

        <Snackbar
            open={snackNoCar}
            autoHideDuration={3000}
            onClose={() => setSnackNoCar(false)}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
            <Alert severity="warning" onClose={() => setSnackNoCar(false)} className="rounded-xl shadow-lg">ลูกค้ารายนี้ยังไม่มีรถในระบบ</Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default PoManagementCreate;
